import { useState, useEffect } from 'react';
import Custombutton from '../../../../../components/custombutton/custombutton';
import useCurrencies from '../../../../../customhooks/useCurrencies';
import Paymentinput from '../../../../../components/paymentinput/paymentinput';
import {
    NGNTradeFXLimit,
    USDTradeFXLimit
} from '../../../../../utils/appconstants';
import { Wallet } from '../../../../../interfaces/Wallet';
import InfoDropdown from '../../shared/InfoDropdown';
import useTimer from 'easytimer-react-hook';

export interface placerequestProps {
    activeStep: number;
    updateStep: (activeStep: number) => void;
    currencyFrom: string;
    setCurrencyFrom: (val: string) => void;
    currencyTo: string;
    setCurrencyTo: (val: string) => void;
    amountToSend: number;
    setAmountToSend: (val: number) => void;
    amountToReceive: number;
    setAmountToReceive: (val: number) => void;
    rate?: number;
    conversionType: number;
    wallets: Wallet[];
    isWalletsLoading: boolean;
    isFetchingRate?: boolean;
}

const infoList = [
    {
        label: 'Guaranteed Rate',
        text: 'Exchange rates are constantly changing during trading hours, FrontEdge strives to convert your money at the best rate available.'
    },
    {
        label: 'SWIFT charge',
        text: '$25 SWIFT fee for international payments'
    },
    {
        label: 'Est Delivery 24hrs',
        text: 'FrontEdge executes your transfers as fast as we can within this time frame, but the delivery date is not a guarantee.'
    }
];

const Placerequest = ({
    activeStep,
    updateStep,
    currencyFrom,
    setCurrencyFrom,
    currencyTo,
    setCurrencyTo,
    amountToSend,
    setAmountToSend,
    amountToReceive,
    setAmountToReceive,
    rate,
    conversionType,
    wallets,
    isWalletsLoading,
    isFetchingRate
}: placerequestProps) => {
    const [timer] = useTimer({
        countdown: true,

        startValues: { seconds: 30 }
    });
    const { isLoading: isLoadingCurrencies, currencies } = useCurrencies();

    const [errors, setErrors] = useState({
        deductFrom: '',
        convert: '',
        receive: ''
    });

    const [finalUnion, setFinalUnion] = useState<any>([]);

    const handleConversionCurrencyChange = (value: string) => {
        const selectedWallet = wallets?.find((item) => item.currency === value);

        if (!selectedWallet) {
            setErrors((prevState) => ({
                ...prevState,
                deductFrom: `You don't have a wallet for ${value}`
            }));
        } else {
            setCurrencyFrom(value);
        }
    };

    const validateToNextStep = () => {
        const newErrors = { ...errors };
        newErrors['convert'] = '';
        newErrors['receive'] = '';
        newErrors['deductFrom'] = '';

        const convertFromBalance =
            wallets.find((item) => item.currency === currencyFrom)
                ?.walletBalance || 0;

        if (currencyFrom === 'NGN' && amountToSend < NGNTradeFXLimit) {
            newErrors[
                'convert'
            ] = `The minimun conversion amount for NGN is ₦${NGNTradeFXLimit.toLocaleString()}`;
        }

        if (currencyFrom === 'USD' && amountToSend < USDTradeFXLimit) {
            newErrors[
                'convert'
            ] = `The minimun conversion amount for USD is $${USDTradeFXLimit.toLocaleString()}`;
        }

        if (currencyTo === 'USD' && amountToReceive < USDTradeFXLimit) {
            newErrors[
                'receive'
            ] = `The minimun conversion amount for USD is $${USDTradeFXLimit.toLocaleString()}`;
        }

        if (currencyTo === 'NGN' && amountToReceive < NGNTradeFXLimit) {
            newErrors[
                'receive'
            ] = `The minimun conversion amount for NGN is $${NGNTradeFXLimit.toLocaleString()}`;
        }

        if (amountToSend > convertFromBalance) {
            newErrors['convert'] =
                'Insufficient funds. Click on "Add Funds" ↗ to fund account.';
        }

        if (conversionType === 2 && currencyFrom === currencyTo) {
            newErrors['receive'] = 'Cannot convert to the same currency';
        }

        if (Object.values(newErrors).every((item) => item === '')) {
            updateStep(activeStep + 1);
        } else {
            setErrors((prevState) => ({
                ...prevState,
                ...newErrors
            }));
        }
    };

    useEffect(() => {
        const walletsCurrencyDetails = wallets.map((wallet) => {
            const currencyInfo = currencies.find(
                (c) => c.currencyCode === wallet.currency
            );
            return {
                ...wallet,
                ...currencyInfo
            };
        });
        setFinalUnion(walletsCurrencyDetails);
    }, [currencies, wallets]);

    useEffect(() => {
        setErrors({
            deductFrom: '',
            convert: '',
            receive: ''
        });
    }, [amountToSend, amountToReceive]);

    return (
        <div className='lg:w-[500px] w-full'>
            <div className='mt-2 px-[16px] pb-[16px]'>
                <Paymentinput
                    currencies={finalUnion}
                    loadingCurrencies={isLoadingCurrencies}
                    label='I want to send'
                    name='convert'
                    error={errors.convert.length > 0}
                    helperText={
                        errors.convert.length > 0 ? errors.convert : undefined
                    }
                    amount={amountToSend}
                    setAmount={setAmountToSend}
                    currency={currencyFrom}
                    setCurrency={handleConversionCurrencyChange}
                />

                {rate ? (
                    <div className='mt-4 py-4 border-l-2 border-dotted border-[#7F7F7F] pl-4'>
                        <p className='leading-[28px] text-black'>
                            1 {currencyFrom} = {rate} {currencyTo}
                        </p>
                        <p className='font-semibold text-[#353542] text-[18px] leading-[28px]'>
                            <p>
                                1 {currencyTo} = {(1 / rate).toFixed(6)}{' '}
                                {currencyFrom}
                            </p>
                        </p>
                    </div>
                ) : null}
                <div className='mt-4'>
                    <Paymentinput
                        currencies={finalUnion}
                        label='Recipient gets'
                        loadingCurrencies={isLoadingCurrencies}
                        name='receive'
                        error={errors.receive.length > 0}
                        helperText={
                            errors.receive.length > 0
                                ? errors.receive
                                : undefined
                        }
                        amount={amountToReceive}
                        setAmount={setAmountToReceive}
                        currency={currencyTo}
                        setCurrency={setCurrencyTo}
                    />
                </div>

                <div className='mt-4'>
                    <Custombutton
                        isLoadingButton
                        loading={false}
                        type='submit'
                        fullWidth
                        variant='contained'
                        disabled={
                            isWalletsLoading ||
                            isLoadingCurrencies ||
                            isFetchingRate ||
                            !amountToSend ||
                            !amountToReceive
                        }
                        buttonText='Continue'
                        styles={{
                            padding: '0.75rem 0.62rem 1rem 0.93rem'
                        }}
                        onClickAction={() => {
                            validateToNextStep();
                        }}
                    />
                </div>
            </div>

            <div className='px-[16px] flex flex-col gap-[16px] mb-[80px] '>
                {infoList.map((item) => (
                    <InfoDropdown text={item.text} label={item.label} />
                ))}
            </div>
        </div>
    );
};

export default Placerequest;
